import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  alpha,
  useTheme,
  Chip,
  CardActionArea,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment
} from "@mui/material";
import AppPaginationBar from "components/AppPaginationBar";
import { Add, Edit, Delete, PlayArrow, OpenInNew, Refresh, FilterList, PictureAsPdf, AttachFile, Info, Download, Visibility } from "@mui/icons-material";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { usePageTitle } from "contexts/PageTitleContext";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
// ─── CHANGE 1: Import the buttonConfig ─────────────────────────────────────────
import buttonConfig from "config/ConfigAccessButton";
// ──────────────────────────────────────────────────────────────────────────────

const defaultCourse = {
  id: null,
  crop: "",
  course_type: "",
  title: "",
  description: "",
  additional_info: "",
  youtube_url: "",
  clicks: 0,
  created_at: null,
  updated_at: null,
  pdf_file: null,
  pdf_name: "",
  existing_pdf_url: null
};

const sanitizeUrl = (url) => {
  try {
    const u = new URL(url);
    return u.toString();
  } catch {
    return "";
  }
};

// Extract YouTube video ID from multiple URL formats
const getYouTubeId = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const pathParts = u.pathname.split("/").filter(Boolean);
    const watchIndex = pathParts.findIndex(p => p === "embed" || p === "v");
    if (watchIndex >= 0 && pathParts[watchIndex + 1]) return pathParts[watchIndex + 1];
  } catch {
    // Ignore invalid YouTube URLs
  }
  return "";
};

const getYouTubeThumbnail = (youtubeUrl) => {
  const id = getYouTubeId(youtubeUrl);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
};

// Helper function to check if course category is Training
const isCourseTraining = (course, courseTypesList) => {
  if (!course || !courseTypesList.length) return false;
  
  let courseTypeId = null;
  
  // Get the course type ID from the course object
  if (course.course_type_id !== undefined && course.course_type_id !== null && course.course_type_id !== "") {
    courseTypeId = course.course_type_id;
  } else if (course.course_type !== undefined && course.course_type !== null && course.course_type !== "") {
    if (typeof course.course_type === 'object' && course.course_type !== null) {
      courseTypeId = course.course_type.id;
    } else {
      courseTypeId = course.course_type;
    }
  }
  
  if (!courseTypeId) return false;
  
  // Find the course type and check if name is "Training"
  const courseType = courseTypesList.find(ct => {
    const ctId = typeof ct.id === 'number' ? ct.id : parseInt(ct.id);
    const typeId = typeof courseTypeId === 'number' ? courseTypeId : parseInt(courseTypeId);
    return ctId === typeId && !isNaN(ctId) && !isNaN(typeId);
  });
  
  return courseType && courseType.name.toLowerCase() === 'training';
};

const KnowledgeHub = () => {
  const theme = useTheme();
  const toast = useToast();
  const { showDeleteConfirmation } = useDeleteConfirmation();
  const { setPageTitle } = usePageTitle();
  
  useEffect(() => {
    setPageTitle('Knowledge Hub');
  }, [setPageTitle]);

  const [courses, setCourses] = useState([]);
  const [crops, setCrops] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultCourse);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingCrops, setLoadingCrops] = useState(false);
  const [selectedCrop, setSelectedCrop] = useState('all');
  const [courseTypes, setCourseTypes] = useState([]);
  const [loadingCourseTypes, setLoadingCourseTypes] = useState(false);
  const [selectedCourseType, setSelectedCourseType] = useState(null);
  const [courseTypeId, setCourseTypeId] = useState(null);
  const [courseTypeModalOpen, setCourseTypeModalOpen] = useState(false);
  const [courseTypeForm, setCourseTypeForm] = useState({ id: null, name: '', description: '' });
  const [courseTypeErrors, setCourseTypeErrors] = useState({});
  const [editingCourseType, setEditingCourseType] = useState(null);
  const [categoriesManagementOpen, setCategoriesManagementOpen] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  const [existingPdfUrl, setExistingPdfUrl] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
  
  const userId = localStorage.getItem("user_id");
  const authToken = localStorage.getItem("access_token");

  // ─── CHANGE 2: Get Knowledge Hub permissions from configButton ─────────────
  // This reads the buttonConfig["Knowledge hub"] object which contains:
  // manage_categories, add_course, edit, delete, open (all booleans)
  const hubConfig = buttonConfig["Knowledge hub"] || {};
  // Helper to safely check if a permission is enabled (defaults to false if not found)
  const can = (action) => !!hubConfig[action];
  // ───────────────────────────────────────────────────────────────────────────

  // Check if selected course type is "Training" (case insensitive)
  const isTrainingCategory = () => {
    if (!form.course_type || !courseTypes.length) return false;
    
    const selectedType = courseTypes.find(ct => {
      const ctId = typeof ct.id === 'number' ? ct.id : parseInt(ct.id);
      const formTypeId = typeof form.course_type === 'number' ? form.course_type : parseInt(form.course_type);
      return ctId === formTypeId && !isNaN(ctId) && !isNaN(formTypeId);
    });
    
    return selectedType && selectedType.name.toLowerCase() === 'training';
  };

  // Fetch crops from API
  const fetchCrops = async () => {
    setLoadingCrops(true);
    try {
      const response = await apiService.get(`${API_ENDPOINTS.GET_crops}?user_id=${userId}`);
      if (response?.success) {
        setCrops(response.data);
      }
    } catch (error) {
      console.error("Error fetching crops:", error);
      // toast.showError("Failed to load crops");
    } finally {
      setLoadingCrops(false);
    }
  };

  // Fetch course types from API
  const fetchCourseTypes = async () => {
    setLoadingCourseTypes(true);
    try {
      const response = await apiService.get(API_ENDPOINTS.GET_course_type);
      if (response?.success && response?.data?.CourseType) {
        const courseTypeList = response.data.CourseType.map(type => ({
          id: type.id,
          name: type.name,
          sequence: type.sequence,
          description: type.description || ''
        }));
        setCourseTypes(courseTypeList);
      }
    } catch (error) {
      console.error("Error fetching course types:", error);
      // toast.showError("Failed to load course types");
    } finally {
      setLoadingCourseTypes(false);
    }
  };

  // Open course type modal for create
  const openAddCourseType = () => {
    setEditingCourseType(null);
    setCourseTypeForm({ id: null, name: '', description: '' });
    setCourseTypeErrors({});
    setCourseTypeModalOpen(true);
  };

  // Open course type modal for edit
  const openEditCourseType = (courseType) => {
    setEditingCourseType(courseType.id);
    setCourseTypeForm({ 
      id: courseType.id, 
      name: courseType.name,
      description: courseType.description || ''
    });
    setCourseTypeErrors({});
    setCourseTypeModalOpen(true);
  };

  // Create or update course type
  const handleSaveCourseType = async () => {
    const e = {};
    if (!courseTypeForm.name.trim()) {
      e.name = "Course category name is required";
    }
    setCourseTypeErrors(e);
    if (Object.keys(e).length > 0) return;

    try {
      let response;
      if (editingCourseType) {
        response = await apiService.put(API_ENDPOINTS.PUT_update_course_type, {
          course_type_id: editingCourseType,
          name: courseTypeForm.name.trim(),
          description: courseTypeForm.description?.trim() || ''
        });
      } else {
        response = await apiService.post(API_ENDPOINTS.POST_create_course_type, {
          name: courseTypeForm.name.trim()
        });
      }

      if (response?.success) {
        const message = editingCourseType ? "Course category updated successfully!" : "Course category added successfully!";
        toast.showSuccess(message);
        setCourseTypeModalOpen(false);
        const wasEditing = editingCourseType;
        const savedName = courseTypeForm.name.trim();
        setCourseTypeForm({ id: null, name: '', description: '' });
        setCourseTypeErrors({});
        setEditingCourseType(null);
        await fetchCourseTypes();
        if (wasEditing && selectedCourseType?.id === wasEditing) {
          setSelectedCourseType({ ...selectedCourseType, name: savedName });
        }
        return true;
      } else {
        const errorMessage = response?.message || (editingCourseType ? "Failed to update course category" : "Failed to add course category");
        toast.showError(errorMessage);
        return false;
      }
    } catch (error) {
      console.error("Error saving course type:", error);
      const errorMessage = editingCourseType ? "Failed to update course category" : "Failed to add course category";
      toast.showError(errorMessage);
      return false;
    }
  };

  // Delete course type
  const handleDeleteCourseType = async (courseTypeId, courseTypeName) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Category",
      message: `Are you sure you want to delete the category "${courseTypeName}"? This action cannot be undone.`,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;

    try {
      const response = await apiService.delete(`${API_ENDPOINTS.DELETE_course_type}?course_type_id=${courseTypeId}`);

      if (response?.success) {
        toast.showSuccess("Course category deleted successfully!");
        if (selectedCourseType?.id === courseTypeId) {
          setSelectedCourseType(null);
          setCourseTypeId(null);
          fetchCourses(selectedCrop, null, 1, pageSize);
        }
        fetchCourseTypes();
      } else {
        toast.showError(response?.message || "Failed to delete course category");
      }
    } catch (error) {
      console.error("Error deleting course type:", error);
      toast.showError("Failed to delete course category");
    }
  };

  // Filter by course type
  const filterCoursesByType = async (typeId, page = 1, pageSizeParam = 10) => {
    await fetchCourses(selectedCrop, typeId, page, pageSizeParam);
  };

  // Fetch courses from API with pagination
  const fetchCourses = async (cropId = 'all', courseTypeIdParam = null, page = 1, pageSizeParam = 10) => {
    setLoading(true);
    try {
      let endpoint = API_ENDPOINTS.GET_courses;
      const params = new URLSearchParams();
      params.set('user_id', userId || '');
      params.set('page', String(page));
      params.set('page_size', String(pageSizeParam));
      if (cropId !== 'all') params.set('crop_id', cropId);
      if (courseTypeIdParam) params.set('course_type_id', String(courseTypeIdParam));
      endpoint += (endpoint.includes('?') ? '&' : '?') + params.toString();

      const response = await apiService.get(endpoint);
      if (response?.success) {
        const coursesData = Array.isArray(response.data) ? response.data : [];
        // Map PDF data correctly
        const mappedCourses = coursesData.map(course => ({
          ...course,
          pdf: course.pdf || course.pdf_file || course.existing_pdf_url || null,
          pdf_file: course.pdf || course.pdf_file || course.existing_pdf_url || null,
          existing_pdf_url: course.pdf || course.pdf_file || course.existing_pdf_url || null
        }));
        setCourses(mappedCourses);
        
        const paginationData = response?.pagination || response?.data?.pagination || {};
        if (Object.keys(paginationData).length > 0) {
          setPagination({
            current_page: paginationData.current_page ?? page,
            page_size: paginationData.page_size ?? pageSizeParam,
            total_items: paginationData.total_items ?? 0,
            total_pages: paginationData.total_pages ?? 1,
            has_next: paginationData.has_next ?? false,
            has_previous: paginationData.has_previous ?? false,
            next_page_number: paginationData.next_page_number ?? null,
            previous_page_number: paginationData.previous_page_number ?? null,
          });
          setCurrentPage(paginationData.current_page ?? page);
          setPageSize(paginationData.page_size ?? pageSizeParam);
        } else {
          const list = Array.isArray(response.data) ? response.data : [];
          setPagination(prev => ({ ...prev, total_items: list.length, total_pages: 1, current_page: 1, page_size: pageSizeParam }));
        }
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      // toast.showError("Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    if (courseTypeId) {
      filterCoursesByType(courseTypeId, value, pageSize);
    } else {
      fetchCourses(selectedCrop, null, value, pageSize);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    if (courseTypeId) {
      filterCoursesByType(courseTypeId, 1, newSize);
    } else {
      fetchCourses(selectedCrop, null, 1, newSize);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchCrops();
    fetchCourses('all', null, 1, 10);
    fetchCourseTypes();
  }, []);

  const resetForm = () => {
    setForm(defaultCourse);
    setErrors({});
    setEditing(null);
    setSelectedPdfFile(null);
    setExistingPdfUrl(null);
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = "Title is required";
    if (!form.description.trim()) e.description = "Description is required";
    if (!form.youtube_url.trim()) e.youtube_url = "YouTube link is required";
    if (form.youtube_url && !sanitizeUrl(form.youtube_url)) e.youtube_url = "Enter a valid URL";
    if (!form.crop) e.crop = "Please select a crop";
    if (!form.course_type) e.course_type = "Please select a course category";
    
    if (isTrainingCategory() && !selectedPdfFile && !form.existing_pdf_url) {
      e.pdf = "PDF material is required for Training category";
    }
    
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const openAdd = () => {
    resetForm();
    if (courseTypeId) {
      setForm({ ...defaultCourse, course_type: courseTypeId });
    }
    setModalOpen(true);
  };

  const openEdit = (course) => {
    setEditing(course.id);
    let courseTypeId = "";
    
    if (course.course_type_id !== undefined && course.course_type_id !== null && course.course_type_id !== "") {
      courseTypeId = course.course_type_id;
    } 
    else if (course.course_type !== undefined && course.course_type !== null && course.course_type !== "") {
      if (typeof course.course_type === 'object' && course.course_type !== null) {
        if (course.course_type.id !== undefined) {
          courseTypeId = course.course_type.id;
        }
      } else {
        courseTypeId = course.course_type;
      }
    }
    
    let pdfUrl = course.pdf || course.pdf_file || course.existing_pdf_url || null;
    let pdfName = "";
    
    if (pdfUrl) {
      const fileName = pdfUrl.split("/").pop().split("?")[0];
      pdfName = fileName.replace(/^\d+_\d+_/, "");
    }
 
    setExistingPdfUrl(pdfUrl);
    
    setForm({
      id: course.id,
      crop: course.crop,
      course_type: courseTypeId,
      title: course.title,
      description: course.description,
      additional_info: course.additional_info || "",
      youtube_url: course.youtube_url,
      clicks: course.clicks,
      created_at: course.created_at,
      updated_at: course.updated_at,
      pdf_name: pdfName,
      existing_pdf_url: pdfUrl,
      pdf_file: pdfUrl
    });
    setModalOpen(true);
  }

  const handleDelete = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Course",
      message: "Are you sure you want to delete this course?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    
    try {
      const response = await apiService.delete(`${API_ENDPOINTS.DELETE_course}?course_id=${id}`);
      if (response?.success) {
        toast.showSuccess("Course deleted successfully!");
        if (courseTypeId) filterCoursesByType(courseTypeId, currentPage, pageSize);
        else fetchCourses(selectedCrop, null, currentPage, pageSize);
      } else {
        toast.showError(response?.message || "Failed to delete course");
      }
    } catch (error) {
      console.error("Error deleting course:", error);
      toast.showError("Failed to delete course");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/pdf') {
        setSelectedPdfFile(file);
        const localUrl = URL.createObjectURL(file);
        setForm({ 
          ...form, 
          pdf_name: file.name, 
          existing_pdf_url: localUrl,
          pdf_file: localUrl
        });
        setExistingPdfUrl(localUrl);
        
        if (errors.pdf) {
          setErrors({ ...errors, pdf: undefined });
        }
      } else {
        toast.showError("Please select a PDF file");
        event.target.value = null;
      }
    }
  };

  const handleRemovePdf = () => {
    if (form.existing_pdf_url && form.existing_pdf_url.startsWith('blob:')) {
      URL.revokeObjectURL(form.existing_pdf_url);
    }
    setSelectedPdfFile(null);
    setExistingPdfUrl(null);
    setForm({ ...form, pdf_name: "", existing_pdf_url: null, pdf_file: null });
    
    if (errors.pdf) {
      setErrors({ ...errors, pdf: undefined });
    }
  };

  const handleViewPdf = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    try {
      const formData = new FormData();
      formData.append('crop', form.crop);
      formData.append('course_type', form.course_type);
      formData.append('title', form.title);
      formData.append('description', form.description);
      if (form.additional_info) {
        formData.append('additional_info', form.additional_info);
      }
      formData.append('youtube_url', form.youtube_url);
      
      if (selectedPdfFile) {
        formData.append('pdf', selectedPdfFile);
      } else if (form.existing_pdf_url && !form.existing_pdf_url.startsWith('blob:')) {
        formData.append('existing_pdf_url', form.existing_pdf_url);
      }

      let response;
      if (editing) {
        formData.append('course_id', editing);
        response = await apiService.put(API_ENDPOINTS.PUT_update_course, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
          }
        });
      } else {
        response = await apiService.post(API_ENDPOINTS.POST_create_course, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
          }
        });
      }

      if (response?.success) {
        toast.showSuccess(editing ? "Course updated successfully!" : "Course added successfully!");
        setModalOpen(false);
        resetForm();
        if (courseTypeId) {
          filterCoursesByType(courseTypeId, currentPage, pageSize);
        } else {
          fetchCourses(selectedCrop, null, currentPage, pageSize);
        }
      } else {
        toast.showError(response?.message || "Failed to save course");
      }
    } catch (error) {
      console.error("Error saving course:", error);
      toast.showError("Failed to save course");
    }
  };

  const handleOpenAndCount = (course) => {
    const safeUrl = sanitizeUrl(course.youtube_url);
    if (!safeUrl) return;
    window.open(safeUrl, '_blank', 'noopener');
  };

  const getCardThumbnail = (course) => {
    return getYouTubeThumbnail(course.youtube_url) || "/static/thumbnail/thumbnail-1.png";
  };

  const getCropName = (cropId) => {
    const crop = crops.find(c => c.id === cropId);
    return crop ? crop.name : 'Unknown Crop';
  };

  const handleCropFilterChange = (cropId) => {
    setSelectedCrop(cropId);
    setCurrentPage(1);
    if (courseTypeId) {
      fetchCourses(cropId, courseTypeId, 1, pageSize);
    } else {
      fetchCourses(cropId, null, 1, pageSize);
    }
  };

  const handleCourseTypeChange = (courseType) => {
    if (courseType) {
      setSelectedCourseType(courseType);
      setCourseTypeId(courseType.id);
      setCurrentPage(1);
      filterCoursesByType(courseType.id, 1, pageSize);
    } else {
      setSelectedCourseType(null);
      setCourseTypeId(null);
      setCurrentPage(1);
      fetchCourses(selectedCrop, null, 1, pageSize);
    }
  };

  return (
    <Box p={2}>
      <Card sx={{ p: { xs: 1.5, sm: 2, md: 2 }, mb: 2, position: 'sticky', top: 72, zIndex: 100, bgcolor: theme.palette.background.paper, backgroundColor: theme.palette.background.paper, backgroundImage: 'none', isolation: 'isolate', border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
            Courses 
          </Typography>
          
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Autocomplete
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 180, md: 200 }, width: { xs: '100%', sm: 'auto' } }}
              options={[{ id: 'all', name: 'All Crops' }, ...crops]}
              getOptionLabel={(option) => option.name}
              value={
                selectedCrop === 'all'
                  ? { id: 'all', name: 'All Crops' }
                  : crops.find(c => c.id === selectedCrop) || null
              }
              onChange={(event, newValue) => {
                const cropId = newValue ? newValue.id : 'all';
                handleCropFilterChange(cropId);
              }}
              loading={loadingCrops}
              ListboxProps={{
                sx: {
                  maxHeight: 200,
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#bdbdbd",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#9e9e9e",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Crop"
                  placeholder="Search crops..."
                  size="small"
                />
              )}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                return options.filter(option =>
                  option.name.toLowerCase().includes(inputValue.toLowerCase())
                );
              }}
            />
          
            <Box display="flex" alignItems="center" gap={1} sx={{ width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: { xs: '100%', sm: 180, md: 200 }, width: { xs: '100%', sm: 'auto' } }}
                options={courseTypes}
                getOptionLabel={(option) => option.name}
                value={selectedCourseType}
                onChange={(event, newValue) => {
                  handleCourseTypeChange(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter Category"
                    placeholder={courseTypes.length === 0 ? "No categories available" : "Select category..."}
                    size="small"
                  />
                )}
                loading={loadingCourseTypes}
                disabled={courseTypes.length === 0 && !loadingCourseTypes}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  return options.filter(option => 
                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />
              {/* ─── CHANGE 3: "Manage Categories" button — disabled when no permission ─── */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={() => can('manage_categories') && setCategoriesManagementOpen(true)}
                disabled={!can('manage_categories')}
                sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
              >
                Manage Categories
              </Button>
              {/* ───────────────────────────────────────────────────────────────── */}
            </Box>
          
            <Button 
              variant="outlined" 
              startIcon={<Refresh />} 
              onClick={() => {
                if (courseTypeId) {
                  filterCoursesByType(courseTypeId, currentPage, pageSize);
                } else {
                  fetchCourses(selectedCrop, null, currentPage, pageSize);
                }
              }}
              disabled={loading}
              sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
            >
              Refresh
            </Button>
          
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 100, md: 110 }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel>Rows</InputLabel>
              <Select value={pageSize} label="Rows" onChange={handlePageSizeChange}>
                {pageSizeOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          
            {/* ─── CHANGE 4: "Add Course" button — disabled when no permission ────── */}
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => can('add_course') && openAdd()}
              disabled={!can('add_course')}
              sx={{
                textTransform: 'none',
                px: { xs: 1.5, sm: 1.5, md: 1.5 },
                py: { xs: 0.8, sm: 1, md: 1.2 },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Add Course
            </Button>
            {/* ───────────────────────────────────────────────────────────────── */}
          </Box>
        </Box>
      </Card>

      {loading ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading courses...</Typography>
        </Card>
      ) : courses.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>No courses found</Typography>
          <Typography color="text.secondary" gutterBottom>
            {selectedCrop !== 'all' 
              ? `No courses available for ${getCropName(selectedCrop)}`
              : "Add your first course with title, description, and YouTube link."
            }
          </Typography>
          {/* ─── CHANGE 5: Empty state "Add Course" — disabled when no permission ─ */}
          <Button 
            variant="contained" 
            startIcon={<Add />} 
            onClick={() => can('add_course') && openAdd()} 
            disabled={!can('add_course')}
            sx={{ textTransform: 'none', mt: 1 }}
          >
            Add Course
          </Button>
          {/* ─────────────────────────────────────────────────────────────────── */}
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {courses.map(course => {
              const isTraining = isCourseTraining(course, courseTypes);
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
                  <Card sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    
                    <CardActionArea onClick={() => handleOpenAndCount(course)}>
                      <Box sx={{ position: 'relative', pt: '56.25%', overflow: 'hidden' }}>
                        <Box
                          component="img"
                          src={getCardThumbnail(course)}
                          alt={course.title}
                          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    </CardActionArea>

                    <Box sx={{ p: 0.3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={course.title}>
                          {course.title}
                        </Typography>
                        <Chip label={`Clicks: ${course.clicks_count || course.clicks || 0}`} size="small" />
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          minHeight: 40,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {course.description}
                      </Typography>

                      {course.additional_info && (
                        <Typography variant="caption" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Info fontSize="inherit" />
                          {course.additional_info}
                        </Typography>
                      )}

                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Chip
                          label={course.crop_name || getCropName(course.crop)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      {/* ACTION SECTION */}
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        gap={1}
                      >
                        
                        {/* LEFT SIDE */}
                        <Box display="flex" alignItems="center" gap={1}>
                          {/* ─── CHANGE 6: "Open" button — disabled when no permission ───── */}
                          <Button
                            size="small"
                            startIcon={<OpenInNew />}
                            onClick={() => can('open') && handleOpenAndCount(course)}
                            disabled={!can('open')}
                            sx={{ textTransform: "none", minWidth: "auto" }}
                          >
                            Open
                          </Button>
                          {/* ─────────────────────────────────────────────────────────── */}

                          {/* Show PDF icon only for Training category courses */}
                          {isTraining && course.pdf && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  display: { xs: 'none', sm: 'block' },
                                }}
                              >
                                View PDF
                              </Typography>
                              <Tooltip title="View PDF">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewPdf(course.pdf)}
                                  sx={{ color: 'info.main' }}
                                >
                                  <PictureAsPdf fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>

                        {/* RIGHT SIDE */}
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {/* ─── CHANGE 7: "Edit" button — disabled when no permission ────── */}
                          <Tooltip title={can('edit') ? "Edit" : "No permission to edit"}>
                            <span>
                              <IconButton 
                                color="editGreen" //add this
                                onClick={() => can('edit') && openEdit(course)} 
                                disabled={!can('edit')}
                                size="small"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* ─────────────────────────────────────────────────────────── */}

                          {/* ─── CHANGE 8: "Delete" button — disabled when no permission ─── */}
                          <Tooltip title={can('delete') ? "Delete" : "No permission to delete"}>
                            <span>
                              <IconButton 
                                color="error"
                                onClick={() => can('delete') && handleDelete(course.id)} 
                                disabled={!can('delete')}
                                size="small"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* ─────────────────────────────────────────────────────────── */}
                        </Box>

                      </Box>

                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <AppPaginationBar
            pagination={pagination}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            itemLabel="courses"
          />
        </>
      )}

      {/* ─── CHANGE 9: Add/Edit Course Modal — disabled save when no permission ─── */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Course' : 'Add Course'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                error={!!errors.title}
                helperText={errors.title}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={courseTypes}
                getOptionLabel={(option) => option.name}
                value={(() => {
                  if (!form.course_type || form.course_type === '') return null;
                  const found = courseTypes.find(ct => {
                    const ctId = typeof ct.id === 'number' ? ct.id : parseInt(ct.id);
                    const formTypeId = typeof form.course_type === 'number' ? form.course_type : parseInt(form.course_type);
                    const matches = ctId === formTypeId && !isNaN(ctId) && !isNaN(formTypeId);
                    return matches;
                  });
                  return found || null;
                })()}
                onChange={(event, newValue) => {
                  setForm({ ...form, course_type: newValue ? newValue.id : '' });
                  if (errors.pdf) {
                    setErrors({ ...errors, pdf: undefined });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Course Category"
                    placeholder="Search and select course category..."
                    error={!!errors.course_type}
                    helperText={errors.course_type}
                  />
                )}
                loading={loadingCourseTypes}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  return options.filter(option => 
                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={crops}
                getOptionLabel={(option) => option.name}
                value={crops.find(c => c.id === form.crop) || null}
                onChange={(event, newValue) => {
                  setForm({ ...form, crop: newValue ? newValue.id : '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Crop"
                    placeholder="Search and select crop..."
                    error={!!errors.crop}
                    helperText={errors.crop}
                  />
                )}
                loading={loadingCrops}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  return options.filter(option => 
                    option.name.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
              />
            </Grid>

            {/* Conditional PDF Upload Section - Only shown for Training category */}
            {isTrainingCategory() && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PictureAsPdf fontSize="small" color="error" />
                  PDF Material <span style={{ color: theme.palette.error.main }}>(Required for Training)</span>
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="No PDF selected"
                    value={form.pdf_name || (form.existing_pdf_url ? 'PDF Available' : '')}
                    disabled={true}
                    sx={{ minWidth: '250px', flex: 1 }}
                    error={!!errors.pdf}
                    helperText={errors.pdf}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (form.existing_pdf_url || form.pdf_name) ? (
                        <InputAdornment position="start">
                          <PictureAsPdf fontSize="small" color="error" />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFile />}
                    size="small"
                    sx={{ textTransform: 'none', minWidth: '140px' }}
                  >
                    {(form.existing_pdf_url || form.pdf_name) ? 'Change PDF' : 'Choose PDF'}
                    <input
                      type="file"
                      hidden
                      accept=".pdf,application/pdf"
                      onChange={handleFileChange}
                    />
                  </Button>
                  
                  {(form.existing_pdf_url || form.pdf_name) && (
                    <Tooltip title="Remove PDF">
                      <IconButton 
                        size="small" 
                        onClick={handleRemovePdf}
                        sx={{ 
                          color: 'error.main',
                          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.1)
                          }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                
                {selectedPdfFile && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                    ✓ New PDF ready to upload: {selectedPdfFile.name} ({(selectedPdfFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="YouTube Link"
                value={form.youtube_url}
                onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                error={!!errors.youtube_url}
                helperText={errors.youtube_url || 'Paste a valid YouTube URL'}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">YouTube Preview</Typography>
              <Box sx={{ mt: 0.5, borderRadius: 1, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.6)}`, width: '100%', maxWidth: 480 }}>
                <Box component="img" src={getYouTubeThumbnail(form.youtube_url) || "/static/thumbnail/thumbnail-1.png"} alt="Preview" sx={{ width: '100%', display: 'block' }} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          {/* ─── CHANGE 9b: Save button disabled when no add/edit permission ─────── */}
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={editing ? !can('edit') : !can('add_course')}
          >
            {editing ? 'Update' : 'Create'}
          </Button>
          {/* ─────────────────────────────────────────────────────────────────────── */}
        </DialogActions>
      </Dialog>

      {/* ─── CHANGE 10: Categories Management Modal — disabled actions when no permission ─ */}
      <Dialog 
        open={categoriesManagementOpen} 
        onClose={() => {
          setCategoriesManagementOpen(false);
          setCourseTypeModalOpen(false);
          setCourseTypeForm({ id: null, name: '' });
          setCourseTypeErrors({});
          setEditingCourseType(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={600}>
              Manage Course Categories
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => can('manage_categories') && openAddCourseType()}
              disabled={!can('manage_categories')}
              sx={{ textTransform: 'none' }}
            >
              Add Category
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingCourseTypes ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : courseTypes.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No categories found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click "Add Category" to create your first category
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => can('manage_categories') && openAddCourseType()}
                disabled={!can('manage_categories')}
                sx={{ textTransform: 'none' }}
              >
                Add Category
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                    <TableCell align="center" sx={{ fontWeight: 600, pl: 3 }}>Sr No</TableCell>  
                    <TableCell sx={{ fontWeight: 600, pl: 2, pr: 1 }}>Category Name</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, pl: 1, pr: 3 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {courseTypes.map((category, index) => (
                    <TableRow 
                      key={category.id} 
                      hover
                      sx={{ 
                        '&:last-child td, &:last-child th': { border: 0 },
                        '& td': { py: 1.5 }
                      }}
                    >
                      <TableCell align="center" sx={{ pl: 3 }}>{index + 1}</TableCell>
                      <TableCell sx={{ pl: 2, pr: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {category.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ pl: 1, pr: 3 }}>
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title={can('manage_categories') ? "Edit Category" : "No permission"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => can('manage_categories') && (() => { openEditCourseType(category); setCategoriesManagementOpen(false); })()}
                                disabled={!can('manage_categories')}
                                sx={{ color: 'primary.main' }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={can('manage_categories') ? "Delete Category" : "No permission"}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => can('manage_categories') && handleDeleteCourseType(category.id, category.name)}
                                disabled={!can('manage_categories')}
                                sx={{ color: 'error.main' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setCategoriesManagementOpen(false);
              setCourseTypeModalOpen(false);
              setCourseTypeForm({ id: null, name: '', description: '' });
              setCourseTypeErrors({});
              setEditingCourseType(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Course Category Creation/Edit Modal */}
      <Dialog 
        open={courseTypeModalOpen} 
        onClose={() => {
          setCourseTypeModalOpen(false);
          setCourseTypeForm({ id: null, name: '', description: '' });
          setCourseTypeErrors({});
          setEditingCourseType(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingCourseType ? 'Edit Course Category' : 'Add New Course Category'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                value={courseTypeForm.name}
                onChange={(e) => setCourseTypeForm({ ...courseTypeForm, name: e.target.value })}
                error={!!courseTypeErrors.name}
                helperText={courseTypeErrors.name || 'Enter a name for the course category'}
                placeholder="e.g., Beginner Courses, Advanced Training, etc."
                autoFocus
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCourseTypeModalOpen(false);
            setCourseTypeForm({ id: null, name: '', description: '' });
            setCourseTypeErrors({});
            setEditingCourseType(null);
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={async () => {
              if (!can('manage_categories')) return;
              const success = await handleSaveCourseType();
              if (success && categoriesManagementOpen) {
                setTimeout(() => {
                  setCategoriesManagementOpen(true);
                }, 100);
              }
            }}
            disabled={!can('manage_categories')}
          >
            {editingCourseType ? 'Update Category' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnowledgeHub;